<template>
  <FilterGroup
    v-if="modelValue"
    :is-collapsible="isCollapsible"
    :badge="modelValue.length"
    :label="filter.name"
    :filter-slug="filter.slug"
    @click:reset="handleReset"
    @click:apply="handleApply"
    @reset-to-initial-state="handleResetToInitialState"
  >
    <MultipleSelectionList
      :model-value="tempModelValue"
      :items="sortedFilterValues"
      :initial-state="modelValue"
      :is-reset="isReset"
      class="mx-2 mt-2 grid gap-3"
      :class="{
        'grid-cols-1': showNewDesktopPlpQuickFilters,
        'grid-cols-2': !showNewDesktopPlpQuickFilters,
      }"
      name="FilterSelectSize"
      role="group"
      :aria-labelledby="`${filter.slug}`"
      :use-deep-is-active-check="true"
      @update:model-value="handleModelValueUpdate"
    >
      <template #item="{ item, toggleItem, isActive }">
        <Checkbox
          :id="item.displayName"
          :value="item.value"
          :checked="isActive"
          :label="`${item.displayName} (${item.count})`"
          @change="toggleItem(item)"
        />
      </template>
    </MultipleSelectionList>
  </FilterGroup>
</template>

<script lang="ts">
import type { ProductFilter } from '@scayle/storefront-nuxt'

import FilterGroup from '~/components/filters/FilterGroup.vue'
import MultipleSelectionList from '#layers/base/components/ui/selectionList/MultipleSelectionList.vue'
import Checkbox from '~/components/ui/forms/inputs/Checkbox.vue'
import { useDeviceType } from '~/composables/useDeviceType'

export default defineComponent({
  name: 'AttributesFilter',
  components: { MultipleSelectionList, FilterGroup, Checkbox },
  props: {
    modelValue: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    filter: {
      type: Object as PropType<ProductFilter>,
      required: true,
    },
    filterValues: {
      type: Array as PropType<any[]>,
      required: true,
    },
    isCollapsible: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  emits: ['reset', 'apply', 'update:modelValue', 'update:modelValueIds'],
  setup(props, { emit }) {
    const isReset = ref<boolean>(false)
    const { showNewDesktopPlpQuickFilters } = useExperiments()
    const { isMobileOrTablet } = useDeviceType()

    const tempModelValue = ref(props.modelValue)

    const sortedFilterValues = computed(() => {
      if (props.filter.name.toLowerCase() === 'marke') {
        const madeleine = props.filterValues.find(
          (item) => item.displayName.toLowerCase() === 'madeleine',
        )
        const remainingBrands = props.filterValues
          .filter((item) => item.displayName.toLowerCase() !== 'madeleine')
          .sort((a, b) => a.displayName.localeCompare(b.displayName))
        return madeleine ? [madeleine, ...remainingBrands] : remainingBrands
      }
      return props.filterValues
    })

    const handleReset = () => {
      isReset.value = false
      tempModelValue.value = props.modelValue

      nextTick(() => {
        isReset.value = true
        emit('reset')
      })
    }

    const handleResetToInitialState = () => {
      isReset.value = false
      tempModelValue.value = props.modelValue

      nextTick(() => {
        isReset.value = true
      })
    }

    const handleApply = () => {
      isReset.value = false

      emit('update:modelValue', tempModelValue.value)
      emit(
        'update:modelValueIds',
        tempModelValue.value.map((item) => item.value),
      )
      emit('apply', {
        values: tempModelValue.value.map((item: any) => item.value),
        displayNames: tempModelValue.value.map((item: any) => item.displayName),
      })
    }

    const handleModelValueUpdate = (newValue: any[]) => {
      tempModelValue.value = newValue

      if (
        !showNewDesktopPlpQuickFilters.value ||
        (showNewDesktopPlpQuickFilters.value && isMobileOrTablet.value)
      ) {
        emit('update:modelValue', tempModelValue.value)
        emit(
          'update:modelValueIds',
          tempModelValue.value.map((item) => item.value),
        )
      }
    }

    watch(
      () => props.modelValue,
      (newModelValue) => {
        tempModelValue.value = newModelValue
      },
      { deep: true },
    )

    return {
      handleReset,
      handleApply,
      handleResetToInitialState,
      handleModelValueUpdate,
      isReset,
      tempModelValue,
      showNewDesktopPlpQuickFilters,
      sortedFilterValues,
    }
  },
})
</script>
